/* Stockists page: list, map, and nearest-store sort.

   Data comes from assets/dico-stockists.json, which already carries a lat/lon for
   every shop. Geocoding happens once, by hand, when a shop is added -- never at
   runtime. A page that geocoded 47 addresses on load would make 47 requests to a
   free service that asks for one per second, and would be at the mercy of it
   being up.

   The map is Leaflet on OpenStreetMap tiles: no API key, no billing account, no
   per-view cost. It is loaded from a CDN and the whole map is skipped if that
   fails -- the list is the page, the map is an aid to it.

   Distance is straight-line (haversine), not driving distance. For "which of
   these is nearest me" that is the right answer and needs no routing service. */
(function () {
    'use strict';

    var root, data = null, map = null, markers = [], here = null;
    var countryOrder = [], cityOrder = [];

    function el(tag, cls, text) {
        var e = document.createElement(tag);
        if (cls) { e.className = cls; }
        if (text != null) { e.textContent = text; }
        return e;
    }

    function parseOrder(raw) {
        return (raw || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }

    /* Sort that honours a hand-set order first and falls back to alphabetical.

       Countries and cities are not naturally alphabetical for this brand: Denmark
       is home and Copenhagen is the flagship city, so an A-Z list buries them.
       Anything named in the theme setting comes first, in the order given; the
       rest follow alphabetically. Danish collation, so Æ, Ø and Å sort after Z
       where a Dane expects them rather than next to A and O. */
    function ranked(names, order) {
        return names.slice().sort(function (a, b) {
            var ia = order.indexOf(a), ib = order.indexOf(b);
            if (ia > -1 || ib > -1) {
                if (ia === -1) { return 1; }
                if (ib === -1) { return -1; }
                return ia - ib;
            }
            return a.localeCompare(b, 'da');
        });
    }

    function km(a, b) {
        var R = 6371, toRad = Math.PI / 180;
        var dLat = (b.lat - a.lat) * toRad;
        var dLon = (b.lon - a.lon) * toRad;
        var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(a.lat * toRad) * Math.cos(b.lat * toRad) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
        return Math.round(R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s)));
    }

    function matches(s, q) {
        if (!q) { return true; }
        q = q.toLowerCase();
        return (s.name + ' ' + s.address + ' ' + s.zip + ' ' + s.city + ' ' + s.country).toLowerCase().indexOf(q) > -1;
    }

    /* One <article> per shop. Kept identical in both views so the markup, and any
       styling of it, has a single definition. */
    function card(s, dist) {
        var a = el('article', 'dico-st-shop');
        a.appendChild(el('h3', 'dico-st-name', s.name));
        var p = el('p', 'dico-st-addr');
        p.appendChild(document.createTextNode(s.address));
        p.appendChild(el('br'));
        p.appendChild(document.createTextNode([s.zip, s.city].filter(Boolean).join(' ')));
        p.appendChild(el('br'));
        p.appendChild(document.createTextNode(s.country));
        a.appendChild(p);
        if (dist != null) { a.appendChild(el('p', 'dico-st-dist', dist + ' km away')); }
        a.addEventListener('click', function () { focus(s); });
        return a;
    }

    function focus(s) {
        if (!map) { return; }
        map.setView([s.lat, s.lon], 14, { animate: true });
        markers.forEach(function (m) { if (m.dicoStore === s) { m.openPopup(); } });
        document.getElementById('dico-st-map').scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* Keep the map showing what the list is showing.

       Searching "odense" and being left looking at the whole of Europe makes the
       map dead weight. Pins that fall out of the search are taken off the map so
       the view is not framed around shops the visitor cannot see in the list, and
       the view is then fitted to what is left. A single match gets a fixed zoom,
       because fitBounds on one point zooms to the maximum and lands the visitor
       in an unrecognisable close-up of a roof. */
    function syncMap(shops) {
        if (!map) { return; }

        var pts = [];
        markers.forEach(function (m) {
            var on = shops.indexOf(m.dicoStore) > -1;
            if (on && !map.hasLayer(m)) { m.addTo(map); }
            if (!on && map.hasLayer(m)) { map.removeLayer(m); }
            if (on) { pts.push([m.dicoStore.lat, m.dicoStore.lon]); }
        });

        if (!pts.length) { return; }

        /* A known location with no search is the visitor asking "what is near me",
           so the view stays on them. A search is a more specific question and wins. */
        var searching = (document.getElementById('dico-st-search').value || '').trim().length > 0;
        if (here && !searching) { return; }

        if (pts.length === 1) {
            map.setView(pts[0], 14, { animate: true });
        } else {
            map.fitBounds(pts, { padding: [30, 30], animate: true });
        }
    }

    function render() {
        var q = (document.getElementById('dico-st-search').value || '').trim();
        var list = document.getElementById('dico-st-list');
        var count = document.getElementById('dico-st-count');
        list.innerHTML = '';

        var shops = data.stores.filter(function (s) { return matches(s, q); });

        count.textContent = shops.length + (shops.length === 1 ? ' stockist' : ' stockists');
        syncMap(shops);

        if (!shops.length) {
            list.appendChild(el('p', 'dico-st-empty', 'No stockists match that search.'));
            return;
        }

        /* With a location known, country/city grouping stops being useful -- the
           question has become "what is closest", so the answer is one flat list in
           distance order. */
        if (here) {
            shops.slice().sort(function (a, b) { return km(here, a) - km(here, b); })
                 .forEach(function (s) { list.appendChild(card(s, km(here, s))); });
            return;
        }

        var byCountry = {};
        shops.forEach(function (s) {
            byCountry[s.country] = byCountry[s.country] || {};
            byCountry[s.country][s.city] = byCountry[s.country][s.city] || [];
            byCountry[s.country][s.city].push(s);
        });

        ranked(Object.keys(byCountry), countryOrder).forEach(function (country) {
            var sec = el('section', 'dico-st-country');
            sec.appendChild(el('h2', 'dico-st-country-name', country));
            ranked(Object.keys(byCountry[country]), cityOrder).forEach(function (city) {
                var c = el('div', 'dico-st-city');
                c.appendChild(el('h3', 'dico-st-city-name', city));
                byCountry[country][city].forEach(function (s) { c.appendChild(card(s, null)); });
                sec.appendChild(c);
            });
            list.appendChild(sec);
        });
    }

    function buildMap() {
        if (typeof window.L === 'undefined') {
            var box = document.getElementById('dico-st-map');
            if (box) { box.style.display = 'none'; }
            return;
        }
        map = window.L.map('dico-st-map', { scrollWheelZoom: false });
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 18,
            attribution: '&copy; OpenStreetMap'
        }).addTo(map);

        data.stores.forEach(function (s) {
            var m = window.L.marker([s.lat, s.lon]);
            m.dicoStore = s;
            m.bindPopup('<strong>' + s.name + '</strong><br>' + s.address + '<br>' + s.zip + ' ' + s.city);
            m.addTo(map);
            markers.push(m);
        });

        /* Framed by the same routine the search uses, so first paint and every
           later view are decided in one place. */
        syncMap(data.stores);
    }

    function locate() {
        var btn = document.getElementById('dico-st-locate');
        var note = document.getElementById('dico-st-note');
        if (!navigator.geolocation) {
            note.textContent = 'Your browser will not share a location.';
            return;
        }
        btn.disabled = true;
        note.textContent = 'Finding you...';
        navigator.geolocation.getCurrentPosition(function (pos) {
            here = { lat: pos.coords.latitude, lon: pos.coords.longitude };
            btn.disabled = false;
            note.textContent = 'Sorted by distance from you.';
            if (map) {
                window.L.circleMarker([here.lat, here.lon], { radius: 7, color: '#111', fillColor: '#111', fillOpacity: 1 })
                        .addTo(map).bindPopup('You are here');
                map.setView([here.lat, here.lon], 8);
            }
            render();
        }, function () {
            btn.disabled = false;
            note.textContent = 'Could not get your location. Search by city instead.';
        }, { timeout: 10000, maximumAge: 600000 });
    }

    function boot() {
        root = document.getElementById('dico-st');
        if (!root) { return; }
        countryOrder = parseOrder(root.getAttribute('data-country-order'));
        cityOrder = parseOrder(root.getAttribute('data-city-order'));
        var src = root.getAttribute('data-src');

        window.fetch(src).then(function (r) { return r.json(); }).then(function (j) {
            data = j;
            document.getElementById('dico-st-search').addEventListener('input', render);
            document.getElementById('dico-st-locate').addEventListener('click', locate);
            render();
            buildMap();
        })['catch'](function () {
            document.getElementById('dico-st-list').textContent = 'The stockist list could not be loaded.';
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
}());